import { format } from "date-fns";
import moment from "moment";
import Calendar from "react-calendar";
import Skeleton from "react-loading-skeleton";
import cancelIcon from "../../../assets/images/cancel-btn.svg";
import { useAppointmentReasons } from "../../../queries";

const slotValue = (slot) => `${slot?.start_time}-${slot?.end_time}`;

/** Appointment rescheduling calendar and time-slot selector. */
export function RescheduleAppointmentModal({
  isOpen,
  calendarValue,
  availableDates = [],
  bookedTimes = [],
  futureDate,
  selectedTime,
  reason,
  reasons: propReasons,
  isLoadingSlots,
  isSubmitting,
  onClose,
  onDateChange,
  onActiveStartDateChange,
  tileClassName,
  onSelectTime,
  generateTimeSlots,
  onReasonChange,
  onSubmit,
}) {
  const { data: fetchedReasons, isLoading: isLoadingReasons } = useAppointmentReasons(
    "reschedule",
    {
      enabled: Boolean(
        isOpen && (!propReasons || propReasons.length === 0),
      ),
      staleTime: 5 * 60 * 1000,
      gcTime: 5 * 60 * 1000,
    }
  );

  if (!isOpen) return null;

  const reasons =
    propReasons && propReasons.length > 0
      ? propReasons
      : fetchedReasons ?? [];
  const showReasonSkeleton = isLoadingReasons && (!propReasons || propReasons.length === 0);

  const renderIntervalSlot = (slot, index, currentTime, isToday) => {
    if (
      isToday &&
      !moment(slot?.start_time, "HH:mm").isSameOrAfter(currentTime)
    ) {
      return null;
    }

    return (
      <li
        key={slot?.id ?? index}
        className={selectedTime === slotValue(slot) ? "active" : ""}
        onClick={() => onSelectTime(slot)}
      >
        <span>{slot?.start_time} to {slot?.end_time}</span>
      </li>
    );
  };

  const renderGeneratedSlots = (slot, currentTime, isToday) =>
    generateTimeSlots(
      slot?.start_time,
      slot?.end_time,
      slot?.intervals,
      slot?.time_slot_type,
    ).map((time, index) => {
      if (isToday && !moment(time, "HH:mm").isSameOrAfter(currentTime)) {
        return null;
      }

      const isBooked = bookedTimes.includes(time);

      return (
        <li
          aria-disabled={isBooked}
          key={`${time}-${index}`}
          className={selectedTime === time ? "active" : ""}
          onClick={() => !isBooked && onSelectTime(time)}
        >
          <span
            style={{
              backgroundColor: isBooked ? "#616161" : "",
              cursor: isBooked ? "not-allowed" : "",
            }}
          >
            {time}
          </span>
        </li>
      );
    });

  const renderAvailability = (slot, index) => {
    const currentTime = moment().add(1, "hour");
    const isToday = moment(calendarValue).isSame(currentTime, "day");

    return slot?.has_interval === 1
      ? renderIntervalSlot(slot, index, currentTime, isToday)
      : renderGeneratedSlots(slot, currentTime, isToday);
  };

  const hasAvailableDates =
    availableDates.length > 0 &&
    !moment(calendarValue).isSameOrAfter(futureDate);

  return (
    <div className="popup holidayPopup ReschedulePopup">
      <div className="popup-content">
        <button
          aria-label="Close reschedule form"
          className="cancelBtn"
          onClick={onClose}
          type="button"
        >
          <img src={cancelIcon} alt="" />
        </button>
        <div className="popupInner">
          <div className="calendar-section">
            <h3>Reschedule</h3>
            <h5>Make date specific changes</h5>
            <div className="overlayBtn" />
            <Calendar
              onChange={onDateChange}
              value={calendarValue}
              tileClassName={tileClassName}
              onActiveStartDateChange={onActiveStartDateChange}
              minDate={new Date()}
            />
          </div>
          <div className="selected-dates">
            <div className="TopHead">
              <h3>
                {format(calendarValue, "EEEE")} {" "}
                <span>{format(calendarValue, "MMMM d, yyyy")}</span>
              </h3>
              <p>Available Time Slot</p>
            </div>

            {isLoadingSlots ? (
              <div className="SkelWrapper">
                <Skeleton count={2} height={20} width="80%" baseColor="#cfd5f9" />
              </div>
            ) : (
              <ul>
                {hasAvailableDates ? (
                  availableDates.map(renderAvailability)
                ) : (
                  <p>No times available for this day.</p>
                )}
              </ul>
            )}

            <div className="BottomSecion">
              <label htmlFor="consultation-reschedule-reason">
                What is reason of appointment reschedule?
              </label>
              {showReasonSkeleton ? (
                <div
                  className="SkelWrapper"
                  data-testid="reschedule-reason-skeleton"
                  style={{ marginTop: "8px", marginBottom: "8px" }}
                >
                  <Skeleton height={40} borderRadius={8} baseColor="#cfd5f9" />
                </div>
              ) : (
                <div className="CustomSelect">
                  <select
                    id="consultation-reschedule-reason"
                    aria-label="Reschedule reason"
                    value={reason ?? ""}
                    onChange={(event) => onReasonChange(event.target.value)}
                  >
                    <option hidden value="">
                      Select reason here
                    </option>
                    {reasons.map((item, index) => (
                      <option key={item?.id ?? `${item?.name}-${index}`} value={item?.name}>
                        {item?.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <button
              className="saveBtn2"
              disabled={isSubmitting || showReasonSkeleton}
              onClick={onSubmit}
              type="button"
            >
              {isSubmitting ? "Please wait..." : "Submit"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
